import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { Counter } from '@adornis/counter/counter.js';
import { Partner } from '@chancenwerk/master-data-management/partner/entities/Partner.js';
import { ReportingGoal } from '@chancenwerk/reporting/entities/ReportingGoal.js';
import { DateTime } from 'luxon';

@Entity()
export class ReportingApplication extends MongoEntity {
  static override _class = 'ReportingApplication';
  static override _collectionName = 'reporting-applications';

  @Field(() => String)
  partnerID: Maybe<string>;

  @DataLoadedField(() => Partner, ({ partnerID }) => partnerID)
  partner: Maybe<Partner>;

  @Field(() => String)
  id: Maybe<string>;

  @Field(type => Float)
  sum: Maybe<number>;

  @Field(type => DateTime)
  start: Maybe<DateTime>;

  @Field(type => DateTime)
  end: Maybe<DateTime>;

  @Field(() => String)
  applicationText: Maybe<string>;

  @Field(() => String)
  goalsText: Maybe<string>;

  @DataLoadedField(
    () => [ReportingGoal],
    ({ _id }) => ({
      applicationID: _id,
    }),
  )
  goals: Maybe<ReportingGoal[]>;

  get kuerzel() {
    return this.partner?.kuerzel + '_FA' + this.id;
  }

  static override async beforeCreate(entity: ReportingApplication) {
    if (!entity.id) {
      const id = await Counter.getIncrementingNumber(`${entity._class}_${entity.partnerID}`)();
      entity.id = id.toString();
    }
    await super.beforeCreate(entity);
  }

  static override async transformBeforeUpdate(
    oldEntity: EntityData<ReportingApplication>,
    update: ReportingApplication,
  ) {
    if (!oldEntity.id) {
      const id = await Counter.getIncrementingNumber(`${oldEntity._class}_${oldEntity.partnerID}`)();
      update.id = id.toString();
    }
  }
}
